import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  PARTNER_TIERS,
  PARTNER_TIER_LABEL,
  type PartnerTier,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/format-api-error";
import { useCreateTier } from "./use-create-tier";

const LEGACY_NONE = "__none__";

// Ports app/admin/tiers/[id]/_components/AddTierDialog.tsx. Adds a new tier
// (matrix column). Slug auto-derives from name; legacy enum mapping is
// optional. Color hex picker omitted in v1 (matches legacy parity — the
// legacy_tier mapping already covers the common case of inheriting one of
// the four legacy levels). Badge upload happens in the column header once
// the tier exists.

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-\s]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 64);
}

export function AddTierDialog({
  programId,
  open,
  onOpenChange,
}: {
  programId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [legacy, setLegacy] = useState<string>(LEGACY_NONE);
  const mutation = useCreateTier();

  function reset() {
    setName("");
    setSlug("");
    setSlugTouched(false);
    setLegacy(LEGACY_NONE);
  }

  function onChangeOpen(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function onNameChange(v: string) {
    setName(v);
    if (!slugTouched) setSlug(slugify(v));
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedSlug = slug.trim();
    if (!trimmedName || !trimmedSlug) return;
    mutation.mutate(
      {
        programId,
        input: {
          name: trimmedName,
          slug: trimmedSlug,
          legacy_tier:
            legacy === LEGACY_NONE
              ? null
              : (legacy as PartnerTier),
          color_hex: null,
          badge_path: null,
        },
      },
      {
        onSuccess: () => {
          toast.success("Tier added");
          reset();
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(formatApiError(err, "Failed to add tier"));
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onChangeOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            <Plus className="-mt-0.5 mr-1 inline h-5 w-5" />
            Add tier
          </DialogTitle>
          <DialogDescription>
            A new column in the matrix. Upload a badge from the tier card
            after creating it.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="t-name">Name</Label>
            <Input
              id="t-name"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="e.g. Founding Partner"
              required
              maxLength={80}
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="t-slug">Slug</Label>
            <Input
              id="t-slug"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              placeholder="founding-partner"
              required
              maxLength={64}
              pattern="^[a-z][a-z0-9-]{1,63}$"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="t-legacy">Map to legacy enum (optional)</Label>
            <Select value={legacy} onValueChange={setLegacy}>
              <SelectTrigger id="t-legacy">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={LEGACY_NONE}>None</SelectItem>
                {PARTNER_TIERS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {PARTNER_TIER_LABEL[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              When this tier is assigned in the default program, the legacy
              partners.tier enum gets this value. Leave as None unless this
              tier intentionally inherits one of the four legacy levels.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onChangeOpen(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || !name.trim() || !slug.trim()}
            >
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add tier
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
